import { useState } from "react";
import { ShoppingCart, Trash2, Check, X, Loader2, ClipboardList, MessageSquare, Package } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useShoppingList, ShoppingItem } from "@/hooks/useShoppingList";
import { useAuth } from "@/hooks/useAuth";
import { useRealtimeSyncContext } from "@/contexts/RealtimeSyncContext";
import { PrioritySelector, PriorityBadge } from "@/components/ui/PrioritySelector";
import { PRIORITY_LEVELS } from "@/constants/priority";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

interface ShoppingListDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type EditingField = null | "reason" | "notes";

function ShoppingItemRow({ 
  item, 
  onToggle, 
  onRemove, 
  onUpdateReason,
  onUpdateNotes,
  onUpdatePriority,
  onAddToCabinet,
  alreadyInCabinet,
  isUpdating
}: { 
  item: ShoppingItem;
  onToggle: () => void;
  onRemove: () => void;
  onUpdateReason: (reason: string) => void;
  onUpdateNotes: (notes: string) => void;
  onUpdatePriority: (priority: number) => void;
  onAddToCabinet: (oilId: string) => Promise<void>;
  alreadyInCabinet: boolean;
  isUpdating: boolean;
}) {
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [reason, setReason] = useState(item.reason || "");
  const [notes, setNotes] = useState(item.notes || "");
  const [pendingCabinetConfirm, setPendingCabinetConfirm] = useState(false);
  const [isAddingToCabinet, setIsAddingToCabinet] = useState(false);

  const handleToggleClick = () => {
    if (!item.is_purchased && !alreadyInCabinet) {
      // Indo para "comprado" e óleo não está no armário → mostrar pergunta
      setPendingCabinetConfirm(true);
    } else {
      // Voltando para "pendente" OU óleo já no armário → toggle direto
      onToggle();
    }
  };

  const handleConfirmWithCabinet = async () => {
    setIsAddingToCabinet(true);
    try {
      await onAddToCabinet(item.oil_id);
      onToggle();
      setPendingCabinetConfirm(false);
      toast.success(`${item.oil_name} adicionado ao seu Armário! 🎉`);
    } finally {
      setIsAddingToCabinet(false);
    }
  };

  const handleConfirmWithoutCabinet = () => {
    onToggle();
    setPendingCabinetConfirm(false);
  };

  const handleSaveReason = () => {
    onUpdateReason(reason);
    setEditingField(null);
  };

  const handleSaveNotes = () => {
    onUpdateNotes(notes);
    setEditingField(null);
  };

  const handlePriorityChange = (priority: number) => {
    onUpdatePriority(priority);
    toast.success("Prioridade atualizada");
  };

  const handleCancelEdit = () => {
    setReason(item.reason || "");
    setNotes(item.notes || "");
    setEditingField(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={cn(
        "flex flex-col gap-3 p-4 rounded-lg border transition-all",
        item.is_purchased && "bg-muted/50 opacity-60",
        pendingCabinetConfirm && "border-primary/40 bg-primary/5"
      )}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={item.is_purchased || pendingCabinetConfirm}
          onCheckedChange={handleToggleClick}
          className="mt-0.5"
          disabled={pendingCabinetConfirm}
        />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={cn(
              "font-medium text-base",
              item.is_purchased && "line-through text-muted-foreground"
            )}>
              {item.oil_name}
            </p>
            {!item.is_purchased && !pendingCabinetConfirm && (
              <PriorityBadge value={item.priority} />
            )}
          </div>
          
          {/* Exibição de motivo e anotação */}
          {editingField === null && !pendingCabinetConfirm && (
            <div className="mt-2 space-y-1.5">
              {item.reason && (
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <ClipboardList className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{item.reason}</span>
                </div>
              )}
              {item.notes && (
                <div className="flex items-start gap-2 text-sm text-foreground/80 bg-accent/30 rounded px-2 py-1.5">
                  <MessageSquare className="h-4 w-4 mt-0.5 shrink-0" />
                  <span className="italic">"{item.notes}"</span>
                </div>
              )}
            </div>
          )}

          {/* Edição de motivo */}
          {editingField === "reason" && (
            <div className="flex flex-col gap-3 mt-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <ClipboardList className="h-4 w-4" />
                Editando motivo
              </div>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: para fazer serum facial, completa receita X..."
                className="h-11 text-base w-full"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={handleCancelEdit} 
                  className="h-10 px-4 gap-2"
                >
                  <X className="h-4 w-4" />
                  Cancelar
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSaveReason} 
                  className="h-10 px-4 gap-2"
                >
                  <Check className="h-4 w-4" />
                  Salvar
                </Button>
              </div>
            </div>
          )}

          {/* Edição de anotação pessoal */}
          {editingField === "notes" && (
            <div className="flex flex-col gap-3 mt-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <MessageSquare className="h-4 w-4" />
                Editando anotação pessoal
              </div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex: comprar em março, pedir para fulano, promoção Terra Flor..."
                className="min-h-[80px] text-base w-full resize-none"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={handleCancelEdit} 
                  className="h-10 px-4 gap-2"
                >
                  <X className="h-4 w-4" />
                  Cancelar
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSaveNotes} 
                  className="h-10 px-4 gap-2"
                >
                  <Check className="h-4 w-4" />
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Botões de ação */}
        {!item.is_purchased && editingField === null && !pendingCabinetConfirm && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setEditingField("reason")}
              title="Editar motivo"
            >
              <ClipboardList className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setEditingField("notes")}
              title="Editar anotação"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-destructive"
              onClick={onRemove}
              title="Remover"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Botão de remover para itens comprados */}
        {item.is_purchased && editingField === null && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-destructive shrink-0"
            onClick={onRemove}
            title="Remover"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Painel de confirmação: adicionar ao armário? */}
      <AnimatePresence>
        {pendingCabinetConfirm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pl-7">
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
                <p className="text-sm font-medium leading-snug">
                  Deseja adicionar <span className="font-semibold">{item.oil_name}</span> ao seu Armário?
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    className="w-full h-11 gap-2"
                    onClick={handleConfirmWithCabinet}
                    disabled={isAddingToCabinet}
                  >
                    {isAddingToCabinet ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Package className="h-4 w-4" />
                    )}
                    Sim, adicionar ao Armário
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full h-11"
                    onClick={handleConfirmWithoutCabinet}
                    disabled={isAddingToCabinet}
                  >
                    Não, apenas marcar como comprado
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Priority selector for non-purchased items */}
      {!item.is_purchased && editingField === null && !pendingCabinetConfirm && (
        <div className="pl-7">
          <p className="text-xs text-muted-foreground mb-2">Prioridade:</p>
          <PrioritySelector
            value={item.priority}
            onChange={handlePriorityChange}
            disabled={isUpdating}
            size="sm"
          />
        </div>
      )}
    </motion.div>
  );
}

export function ShoppingListDrawer({ open, onOpenChange }: ShoppingListDrawerProps) {
  const { isAuthenticated } = useAuth();
  const { addToCabinetByType, cabinetOils, cabinetVegetableOils, cabinetNeutralBases, cabinetSupplies } = useRealtimeSyncContext();

  const isItemInCabinet = (item: ShoppingItem) => {
    const type = item.item_type || "essential_oil";
    switch (type) {
      case "vegetable_oil": return cabinetVegetableOils.includes(item.oil_id);
      case "neutral_base": return cabinetNeutralBases.includes(item.oil_id);
      case "supply": return cabinetSupplies.includes(item.oil_id);
      default: return cabinetOils.includes(item.oil_id);
    }
  };
  const { 
    pendingItems, 
    purchasedItems, 
    isLoading,
    isUpdating,
    togglePurchased, 
    removeItem, 
    updateItem,
    setPriority,
    clearPurchased,
  } = useShoppingList();

  const handleUpdateReason = (id: string, reason: string) => {
    updateItem({ id, updates: { reason } });
    toast.success("Motivo atualizado");
  };

  const handleUpdateNotes = (id: string, notes: string) => {
    updateItem({ id, updates: { notes } });
    toast.success("Anotação salva");
  };

  const handleUpdatePriority = (id: string, priority: number) => {
    setPriority(id, priority);
  };

  if (!isAuthenticated) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Lista de Compras
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
            <ShoppingCart className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground text-lg">
              Faça login para salvar sua lista de compras
            </p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Lista de Compras
            {pendingItems.length > 0 && (
              <Badge variant="secondary">{pendingItems.length}</Badge>
            )}
          </SheetTitle>
          <SheetDescription className="text-base">
            Organize os óleos que você deseja comprar por prioridade
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-200px)] mt-4">
            {/* Pending items */}
            {pendingItems.length === 0 && purchasedItems.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground mb-2 text-lg">
                  Sua lista está vazia
                </p>
                <p className="text-base text-muted-foreground">
                  Adicione óleos da aba "Lista de Compras" no Meu Armário
                </p>
              </div>
            ) : (
              <div className="space-y-4 pr-4">
                {pendingItems.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground">
                      A COMPRAR ({pendingItems.length})
                    </h4>
                    <AnimatePresence mode="popLayout">
                      {pendingItems.map((item) => (
                        <ShoppingItemRow
                          key={item.id}
                          item={item}
                          onToggle={() => togglePurchased(item.id)}
                          onRemove={() => removeItem(item.id)}
                          onUpdateReason={(reason) => handleUpdateReason(item.id, reason)}
                          onUpdateNotes={(notes) => handleUpdateNotes(item.id, notes)}
                          onUpdatePriority={(priority) => handleUpdatePriority(item.id, priority)}
                          onAddToCabinet={(oilId) => addToCabinetByType(oilId, item.item_type || "essential_oil")}
                          alreadyInCabinet={isItemInCabinet(item)}
                          isUpdating={isUpdating}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                )}

                {purchasedItems.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-muted-foreground">
                          COMPRADOS ({purchasedItems.length})
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => clearPurchased()}
                          className="text-sm h-9"
                        >
                          Limpar
                        </Button>
                      </div>
                      <AnimatePresence mode="popLayout">
                        {purchasedItems.map((item) => (
                          <ShoppingItemRow
                            key={item.id}
                            item={item}
                            onToggle={() => togglePurchased(item.id)}
                            onRemove={() => removeItem(item.id)}
                            onUpdateReason={(reason) => handleUpdateReason(item.id, reason)}
                            onUpdateNotes={(notes) => handleUpdateNotes(item.id, notes)}
                            onUpdatePriority={(priority) => handleUpdatePriority(item.id, priority)}
                            onAddToCabinet={(oilId) => addToCabinetByType(oilId, item.item_type || "essential_oil")}
                            alreadyInCabinet={isItemInCabinet(item)}
                            isUpdating={isUpdating}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  </>
                )}
              </div>
            )}
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
}
